/**
 * React hook for listening to player stats updates via Server-Sent Events (SSE)
 * When the Python bot updates player statistics, this hook will trigger a callback
 * to refresh the data without reloading the page.
 */
import { useEffect, useRef } from 'react';
import { API_ENDPOINTS } from '../api/config';

/**
 * Hook to listen for player stats updates from backend
 * 
 * @param {Function} onStatsUpdated - Callback function called when stats are updated
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether to enable the connection (default: true)
 * @param {number} options.reconnectDelay - Delay in ms before reconnecting (default: 3000)
 */
export const usePlayerStatsUpdates = (onStatsUpdated, options = {}) => {
  const {
    enabled = true,
    reconnectDelay = 3000
  } = options;

  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Get backend URL from config
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    if (!backendUrl) {
      console.warn('REACT_APP_BACKEND_URL not configured, SSE connection disabled');
      return;
    }

    const connect = () => {
      try {
        // Close existing connection if any
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
        }

        // Create SSE connection
        const sseUrl = `${backendUrl}/api/webhooks/events`;
        const eventSource = new EventSource(sseUrl);
        eventSourceRef.current = eventSource;

        // Listen for player stats updates
        eventSource.addEventListener('player_stats_updated', (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('Player stats updated:', data);
            
            // Call the callback to refresh data
            if (onStatsUpdated && typeof onStatsUpdated === 'function') {
              onStatsUpdated(data);
            }
          } catch (error) {
            console.error('Error parsing SSE event data:', error);
          }
        });

        // Handle connection open
        eventSource.onopen = () => {
          console.log('SSE connection established for player stats updates');
          // Clear any pending reconnect
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
          }
        };

        // Handle connection errors
        eventSource.onerror = (error) => {
          console.error('SSE connection error:', error);
          
          // Close the connection
          eventSource.close();
          eventSourceRef.current = null;

          // Reconnect after delay
          if (enabled) {
            reconnectTimeoutRef.current = setTimeout(() => {
              console.log('Reconnecting to SSE...');
              connect();
            }, reconnectDelay);
          }
        };

      } catch (error) {
        console.error('Error creating SSE connection:', error);
        
        // Retry connection after delay
        if (enabled) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, reconnectDelay);
        }
      }
    };

    // Start connection
    connect();

    // Cleanup on unmount
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [enabled, onStatsUpdated, reconnectDelay]);

  // Return connection status
  return {
    isConnected: eventSourceRef.current?.readyState === 1 // EventSource.OPEN = 1
  };
};

export default usePlayerStatsUpdates;
